import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

export type Appearance = 'system' | 'light' | 'dark';
type ResolvedTheme = 'light' | 'dark';

interface AppearanceState {
  appearance: Appearance;
  resolvedTheme: ResolvedTheme;
}

const STORAGE_KEY = 'planlets.appearance';
const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#F7F7F4',
  dark: '#191F1C',
};

function readAppearance(): Appearance {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // Private browsing or an unavailable storage service must not prevent using the app.
  }
  return 'system';
}

function systemQuery(): MediaQueryList | null {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)');
  } catch {
    return null;
  }
}

function resolveTheme(appearance: Appearance): ResolvedTheme {
  if (appearance !== 'system') return appearance;
  return systemQuery()?.matches ? 'dark' : 'light';
}

function readState(): AppearanceState {
  const appearance = readAppearance();
  return { appearance, resolvedTheme: resolveTheme(appearance) };
}

function applyAppearance({ appearance, resolvedTheme }: AppearanceState): void {
  document.documentElement.dataset['appearance'] = appearance;
  document.documentElement.dataset['theme'] = resolvedTheme;
  document.documentElement.style.colorScheme = resolvedTheme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[resolvedTheme]);
}

/** Apply the saved or OS appearance before React renders, including native form controls. */
export function initializeAppearance(): void {
  applyAppearance(readState());
}

/** The nonsecret preference belongs to this install; only System follows subsequent OS changes. */
export function useAppearance(): AppearanceState & { setAppearance: (value: Appearance) => void } {
  const [state, setState] = useState(readState);

  const setAppearance = useCallback((appearance: Appearance) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, appearance);
    } catch {
      // The choice still applies for this session when persistence is unavailable.
    }
    setState({ appearance, resolvedTheme: resolveTheme(appearance) });
  }, []);

  useEffect(() => {
    if (state.appearance !== 'system') return;
    const query = systemQuery();
    if (query === null) return;
    const update = () => {
      setState((current) => current.appearance === 'system'
        ? { appearance: 'system', resolvedTheme: query.matches ? 'dark' : 'light' }
        : current);
    };
    update();
    try {
      query.addEventListener('change', update);
    } catch {
      // Older or restricted hosts can still use the current OS value and explicit choices.
      return;
    }
    return () => {
      try {
        query.removeEventListener('change', update);
      } catch {
        // A replaced host service must not break component teardown.
      }
    };
  }, [state.appearance]);

  useLayoutEffect(() => applyAppearance(state), [state.appearance, state.resolvedTheme]);

  return { ...state, setAppearance };
}
